import React, { 
  useEffect, 
  useRef, 
  useState, 
  useCallback 
} from 'react';
import { supabase } from './supabase';
import { 
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Paper,
  Toolbar,
  Typography,
  useTheme,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Switch,
  Tab,
  Tabs,
  TextField,
  ThemeProvider,
  createTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import ShieldIcon from '@mui/icons-material/Shield';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SendIcon from '@mui/icons-material/Send';
import LogoutIcon from '@mui/icons-material/Logout';
import BugReportIcon from '@mui/icons-material/BugReport';
import MenuIcon from '@mui/icons-material/Menu';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from '@mui/material/styles/styled';
import { 
  AuthErrorBoundary,
  ChatErrorBoundary,
  PropertyErrorBoundary,
  SearchErrorBoundary 
} from './components/ErrorBoundaries';
import { 
  ChatMessage, 
  PropertyData, 
  Property, 
  DBChatMessage 
} from './types';
import { HomeAnalysis } from './components/HomeAnalysis';
import { API_ENDPOINTS } from './config';
import { LoginForm } from './components/LoginForm';
import { WelcomeModal } from './components/WelcomeModal';
import BidRequestTool from './components/BidRequestTool';
import FrontendTest from './components/FrontendTest';

// Create a modern theme with better colors and styling
const theme = createTheme({
  palette: {
    primary: {
      main: '#2E7D32', // Forest green
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    secondary: {
      main: '#FF6F00', // Warm orange for fire-related UI elements
      light: '#FF9800',
      dark: '#E65100',
    },
    background: {
      default: '#F5F7FA',
      paper: '#FFFFFF',
    },
    error: {
      main: '#D32F2F',
    },
    text: {
      primary: '#1A2027',
      secondary: '#4A5568',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#1A2027',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          // Default padding that will be overridden on mobile
          padding: '24px',
          // Remove padding on mobile devices
          '@media (max-width: 600px)': {
            padding: '8px',
            borderRadius: '8px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        },
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ResoFact {
  factName: string;
  factValue: string;
}

interface PropertyFact {
  name: string;
  value: string;
}

interface DebugModalProps {
  open: boolean;
  onClose: () => void;
  propertyData: Array<{ key: string; value: any }> | null;
  rawZillowData: any;
}

declare global {
  interface Window {
    webkitSpeechRecognition: any;
  }
}

const DebugModal: React.FC<DebugModalProps> = ({ open, onClose, propertyData, rawZillowData }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>Debug Information</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Formatted Property Data:
          </Typography>
          {propertyData ? (
            <Grid container spacing={2}>
              {propertyData.map(({ key, value }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" color="primary" gutterBottom>
                        {key}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="text.secondary">No formatted data available</Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            Raw Zillow Data:
          </Typography>
          {rawZillowData ? (
            <Card>
              <CardContent>
                <Typography 
                  variant="body2" 
                  component="pre" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    backgroundColor: '#f5f5f5',
                    p: 2,
                    borderRadius: 1,
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}
                >
                  {JSON.stringify(rawZillowData, null, 2)}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Typography color="text.secondary">No raw data available</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const MarkdownContainer = styled('div')(({ theme }) => ({
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  fontSize: 'inherit',
  '& p': { 
    margin: '0.5em 0',
    lineHeight: 1.6 
  },
  '& ul, & ol': { 
    margin: '0.5em 0',
    paddingLeft: '1.5em'
  },
  '& li': { 
    margin: '0.25em 0',
    lineHeight: 1.6
  },
  '& strong': { 
    color: '#2c3e50',
    fontWeight: 600
  },
  '& a': {
    color: '#2196f3',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    margin: '1em 0 0.5em',
    fontWeight: 600,
    lineHeight: 1.3
  },
  '& hr': {
    margin: '1em 0',
    borderColor: theme.palette.divider
  }
}));

interface ErrorBoundaryProps {
  name: string;
  fallback?: React.ReactElement;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return this.props.fallback || (
        <div>
          <h2>Something went wrong.</h2>
          <details style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error.message}
            {this.state.error.stack}
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  const [user, setUser] = useState<any>(null);
  const [address, setAddress] = useState('');
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [currentPropertyId, setCurrentPropertyId] = useState<string | null>(null);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [propertyData, setPropertyData] = useState<Array<{ key: string; value: any }> | null>(null);
  const [rawZillowData, setRawZillowData] = useState<any>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [savedProperties, setSavedProperties] = useState<Property[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<Property | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' }>({ message: '', severity: 'success' });
  const [debugAnchorEl, setDebugAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [tabValue, setTabValue] = useState(0);
  const [goals, setGoals] = useState<string[]>([]);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [debugMode, setDebugMode] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const recognitionRef = useRef<any>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Listen for path changes
  useEffect(() => {
    const handlePathChange = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePathChange);
    
    return () => {
      window.removeEventListener('popstate', handlePathChange);
    };
  }, []);

  const formatArea = (value: string | number | null | undefined, defaultUnit: string = 'sqft'): string => {
    if (!value) return 'N/A';
    const stringValue = value.toString();
    
    // If it already has a unit (e.g. "0.34 Acres"), return as is
    if (stringValue.includes(' ')) return stringValue;
    
    // Otherwise add the default unit and format number
    const num = parseFloat(stringValue);
    return isNaN(num) ? 'N/A' : `${num.toLocaleString()} ${defaultUnit}`;
  };

  const getPropertyValue = (name: string, data: any = null): string => {
    const details = data?.propertyDetails || rawZillowData?.propertyDetails;
    console.log('Getting property value for:', name, 'Data:', details);
    
    if (!details?.resoFacts) {
      console.log('No property details available');
      return 'N/A';
    }

    const resoFacts = details.resoFacts;
    
    // Check otherFacts first - this is where most of the data is
    if (resoFacts.otherFacts) {
      const fact = resoFacts.otherFacts.find((f: any) => f.name === name);
      if (fact) {
        console.log(`Found value in otherFacts for ${name}:`, fact.value);
        return formatValue(fact.value);
      }
    }

    // Fallback to direct fields if not found in otherFacts
    const directFields: { [key: string]: any } = {
      'Stories': resoFacts.stories,
      'Architecture': resoFacts.architecturalStyle,
      'CONSTRUCTION/EXT': resoFacts.constructionMaterials,
      'FOUNDATION': resoFacts.foundationDetails,
      'ROOF': resoFacts.roofType,
      'FIREPLACE': resoFacts.fireplaceFeatures,
      'GARAGE/ PARKING': resoFacts.parkingFeatures,
      'TOPOGRAPHY': resoFacts.topography,
      'EXTERIOR': resoFacts.exteriorFeatures,
      'APPLIANCES': resoFacts.appliances,
      'FLOOR COVERINGS': resoFacts.flooring,
      'HOA AMENITIES': resoFacts.associationAmenities,
      'DECKS': resoFacts.patioAndPorchFeatures,
      'GAS': resoFacts.gas,
      'HEATING': resoFacts.heating,
      'OTHER': resoFacts.otherEquipment,
      'WATER': resoFacts.waterSource,
      'LAUNDRY': resoFacts.laundryFeatures,
      'BONUS ROOMS': resoFacts.rooms,
      'SEWER': resoFacts.sewer,
      'PROPERTY CONDITION': resoFacts.propertyCondition,
      'COOLING': resoFacts.cooling,
      'POOL': resoFacts.poolFeatures,
      'SECURITY': resoFacts.securityFeatures,
      'WINDOWS': resoFacts.windowFeatures,
      'VIEW': resoFacts.view
    };
    
    const value = directFields[name];
    console.log(`Found value in directFields for ${name}:`, value);
    return formatValue(value);
  };

  const formatValue = (value: string | string[] | null | boolean): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ') || 'N/A';
    return value.toString();
  };

  useEffect(() => {
    // Get initial session with improved error handling
    const initializeAuth = async () => {
      try {
        // First get the current session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error getting initial session:', error);
          return;
        }
        
        // If we have a session, use it
        if (session) {
          console.log('Initial session found');
          setSession(session);
          setUser(session.user);
        } else {
          // Clear any invalid state
          setSession(null);
          setUser(null);
          localStorage.removeItem('supabase.auth.token');
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      }
    };

    initializeAuth();
    
    // Listen for auth changes with improved handling
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      // Safe logging that doesn't expose sensitive tokens
      if (import.meta.env.DEV) {
        // In development, log the event type and just the user ID if available
        console.log('Auth state changed:', event, currentSession ? 
          `User: ${currentSession.user?.id}` : 'No session');
      } else {
        // In production, only log the event type without session details
        console.log('Auth state changed:', event);
      }
      
      if (event === 'SIGNED_OUT') {
        // Clear state
        setSession(null);
        setUser(null);
        setChatMessages([]);
        setCurrentPropertyId(null);
        localStorage.removeItem('supabase.auth.token');
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          // After successful auth state change, immediately try to load properties
          await loadSavedProperties();
        }
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event: any) => {
        const transcript = Array.from(event.results)
          .map((result: any) => result[0])
          .map(result => result.transcript)
          .join('');
        
        setUserInput(transcript);
      };

      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    if (user) {
      loadSavedProperties();
    }
  }, [user]);

  useEffect(() => {
    const handlePropertyDetailsUpdate = (event: any) => {
      if (event.detail) {
        setRawZillowData(event.detail);
      }
    };

    window.addEventListener('updatePropertyDetails', handlePropertyDetailsUpdate);
    return () => {
      window.removeEventListener('updatePropertyDetails', handlePropertyDetailsUpdate);
    };
  }, []);

  useEffect(() => {
    const loadPropertyDetails = async () => {
      if (!currentPropertyId) {
        setRawZillowData(null);
        return;
      }

      try {
        const { data: property, error } = await supabase
          .from('properties')
          .select('*')
          .eq('id', currentPropertyId)
          .single();

        if (error) throw error;
        if (!property) throw new Error('Property not found');

        console.log('Loading saved property data:', property);
        if (property.zillow_data) {
          console.log('Setting rawZillowData from saved property:', property.zillow_data);
          setRawZillowData(property.zillow_data);
        }
      } catch (error) {
        console.error('Error loading property details:', error);
        setError('Failed to load property details');
      }
    };

    loadPropertyDetails();
  }, [currentPropertyId]);

  const loadSavedProperties = useCallback(async () => {
    if (!user) return;
    
    setLoadingProperties(true);
    try {
      console.log('Loading properties for user:', user.id);
      
      const { data: properties, error } = await supabase
        .from('properties')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Error loading properties:', error);
        throw error;
      }
      
      // Normalize the data structure for each property
      const normalizedProperties = properties?.map(property => ({
        ...property,
        zillow_data: {
          ...property.zillow_data,
          propertyDetails: {
            ...property.zillow_data?.propertyDetails,
            resoFacts: property.zillow_data?.propertyDetails?.resoFacts || {},
            address: property.zillow_data?.propertyDetails?.address || {
              streetAddress: property.zillow_data?.propertyDetails?.streetAddress,
              city: property.zillow_data?.propertyDetails?.city,
              state: property.zillow_data?.propertyDetails?.state,
              zipcode: property.zillow_data?.propertyDetails?.zipcode
            }
          }
        }
      }));
      
      setSavedProperties(normalizedProperties || []);
    } catch (error) {
      console.error('Error loading saved properties:', error);
      setError(`Failed to load properties: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingProperties(false);
    }
  }, [user]);

  useEffect(() => {
    void loadSavedProperties();
  }, [loadSavedProperties]);

  const fetchAddressData = async (address: string) => {
    try {
      const apiKey = import.meta.env.VITE_ZILLOW_API_KEY;
      const apiHost = import.meta.env.VITE_ZILLOW_API_HOST;

      if (!apiKey || !apiHost) {
        throw new Error('Zillow API configuration is missing');
      }

      const url = `https://${apiHost}/pro/byaddress?propertyaddress=${encodeURIComponent(address)}`;
      const options = {
        method: 'GET',
        headers: {
          'x-rapidapi-key': apiKey,
          'x-rapidapi-host': apiHost
        }
      };

      console.log('Fetching Zillow data for:', address);
      const response = await fetch(url, options);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Full API Response:', result);
      
      if (!result.propertyDetails) {
        throw new Error('No property details found in API response');
      }

      // Validate and normalize the data structure
      const normalizedData = {
        ...result,
        propertyDetails: {
          ...result.propertyDetails,
          resoFacts: result.propertyDetails.resoFacts || {},
          address: result.propertyDetails.address || {},
        }
      };

      console.log('Normalized Zillow Data:', normalizedData);
      
      // Save the normalized response
      setRawZillowData(normalizedData);

      // Return the normalized result
      return normalizedData;
    } catch (error) {
      console.error('Error fetching address data:', error);
      throw error;
    }
  };

  const generatePrompt = (address: string, propertyDetails: any): string => {
    return `Please analyze my property and provide specific wildfire safety recommendations. Consider these property details:

- Address: ${propertyDetails?.address || address}
- City/State: ${propertyDetails?.cityState || 'N/A'}
- Zip Code: ${propertyDetails?.zipCode || 'N/A'}
- Price: ${propertyDetails?.price || 'N/A'}
- Year Built: ${propertyDetails?.yearBuilt || 'N/A'}
- Stories: ${propertyDetails?.stories || 'N/A'}
- Construction Type: ${propertyDetails?.construction || 'N/A'}
- Foundation: ${propertyDetails?.foundation || 'N/A'}
- Exterior Materials: ${propertyDetails?.exterior || 'N/A'}
- Roof: ${propertyDetails?.roof || 'N/A'}
- Living Area: ${propertyDetails?.livingArea || 'N/A'}
- Lot Size: ${propertyDetails?.lotSize || 'N/A'}
- Architecture Style: ${propertyDetails?.architecture || 'N/A'}
- Property Condition: ${propertyDetails?.propertyCondition || 'N/A'}
- Surrounding Topography: ${propertyDetails?.topography || 'N/A'}
- Surrounding Vegetation: ${propertyDetails?.vegetation || 'N/A'}

Based on these specific characteristics of my property, what are the most important wildfire safety recommendations? Please include:
1. Structural improvements specific to my home's construction and materials
2. Vegetation management based on my property's surroundings
3. Specific safety measures based on the topography
4. Any special considerations based on the property's age and architecture

Please include citations to reliable sources.`;
  };

  const generateFollowUpQuestion = (recommendations: string[]) => {
    return `Based on these recommendations, which areas of your home's wildfire safety would you like to focus on improving first? I can provide more specific guidance for any particular aspect.`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setError('Please sign in to use this feature');
      return;
    }

    setLoading(true);
    setError('');
    setChatMessages([]);
    
    try {
      let details;
      let propertyDetails;
      
      // Check if we already have the data (for saved properties)
      if (rawZillowData?.propertyDetails) {
        details = rawZillowData.propertyDetails;
      } else {
        // Fetch new data from Zillow API
        const zillowData = await fetchAddressData(address);
        setRawZillowData(zillowData);
        details = zillowData?.propertyDetails;
      }

      if (details) {
        // Set property data for display
        const basicInfo = [
          { key: 'Address', value: details.streetAddress || details.address?.streetAddress || address },
          { key: 'City/State', value: `${details.city || details.address?.city || ''}, ${details.state || details.address?.state || ''}` },
          { key: 'Zip Code', value: details.zipcode || details.address?.zipcode || 'N/A' },
          { key: 'Price', value: details.price ? `$${details.price.toLocaleString()}` : 'N/A' },
          { key: 'Bedrooms', value: details.bedrooms?.toString() || 'N/A' },
          { key: 'Bathrooms', value: details.bathrooms?.toString() || 'N/A' },
          { key: 'Year Built', value: details.yearBuilt?.toString() || 'N/A' },
          { key: 'Living Area', value: formatArea(details.livingArea || details.resoFacts?.livingArea) },
          { key: 'Lot Size', value: formatArea(details.lotSize || details.resoFacts?.lotSize, 'acres') }
        ];

        // Get other facts from resoFacts
        const otherFacts = details.resoFacts?.otherFacts?.map(fact => ({
          key: fact.name,
          value: fact.value
        })) || [];

        // Add direct fields if they exist and aren't already in otherFacts
        const directFields = {
          'Stories': details.resoFacts?.stories,
          'Architecture': details.resoFacts?.architecturalStyle,
          'Construction': details.resoFacts?.constructionMaterials,
          'Foundation': details.resoFacts?.foundationDetails,
          'Roof': details.resoFacts?.roofType,
          'Exterior': details.resoFacts?.exteriorFeatures,
          'Property Condition': details.resoFacts?.propertyCondition,
          'Topography': details.resoFacts?.topography,
          'Vegetation': details.resoFacts?.vegetation,
          'Garage/Parking': details.resoFacts?.parkingFeatures,
          'Fireplace': details.resoFacts?.fireplaceFeatures,
          'Appliances': details.resoFacts?.appliances,
          'Floor Coverings': details.resoFacts?.flooring,
          'HOA Amenities': details.resoFacts?.associationAmenities,
          'Decks': details.resoFacts?.patioAndPorchFeatures,
          'Gas': details.resoFacts?.gas,
          'Heating': details.resoFacts?.heating,
          'Other Equipment': details.resoFacts?.otherEquipment,
          'Water': details.resoFacts?.waterSource,
          'Laundry': details.resoFacts?.laundryFeatures,
          'Bonus Rooms': details.resoFacts?.rooms,
          'Sewer': details.resoFacts?.sewer,
          'Cooling': details.resoFacts?.cooling,
          'Pool': details.resoFacts?.poolFeatures,
          'Security': details.resoFacts?.securityFeatures,
          'Windows': details.resoFacts?.windowFeatures,
          'View': details.resoFacts?.view
        };

        const additionalFacts = Object.entries(directFields)
          .filter(([key, value]) => value && !otherFacts.some(fact => fact.key === key))
          .map(([key, value]) => ({
            key,
            value: formatValue(value)
          }));

        // Set the property data - show all non-empty values
        setPropertyData([...basicInfo, ...otherFacts, ...additionalFacts]
          .filter(fact => fact.value && fact.value !== 'N/A' && fact.value !== ', '));

        // Start analysis if we have valid property data
        if (basicInfo.some(info => info.value && info.value !== 'N/A')) {
          await analyzeProperty(details);
        } else {
          setError('Could not find enough property data to analyze');
        }
      } else {
        setError('Could not fetch property details');
      }
    } catch (error) {
      console.error('Error submitting:', error);
      setError('Failed to analyze property');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    try {
      setIsLoading(true);
      setUserInput('');

      // Add user message to chat
      const userMessage: ChatMessage = {
        role: 'user',
        content: message,
        created_at: new Date().toISOString()
      };

      const updatedMessages: ChatMessage[] = [...chatMessages, userMessage];
      setChatMessages(updatedMessages);

      // If we have a property context, save the message
      if (user && currentPropertyId) {
        const { error: saveError } = await supabase
          .from('chat_messages')
          .insert({
            property_id: currentPropertyId,
            user_id: user.id,
            role: 'user',
            content: message,
            created_at: new Date().toISOString()
          });

        if (saveError) console.error('Error saving message:', saveError);
      }

      // Call API to get assistant response
      const response = await fetch(API_ENDPOINTS.chat, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          propertyId: currentPropertyId || null,
          propertyDetails: currentPropertyId ? propertyData : null,
          userId: user?.id || null,
          previousMessages: chatMessages
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from chat API');
      }

      const data = await response.json();
      
      // Add assistant response to chat
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.message,
        created_at: new Date().toISOString()
      };

      const newMessages = [...updatedMessages, assistantMessage];
      setChatMessages(newMessages);

      // If we have a property context, save the assistant message
      if (user && currentPropertyId) {
        const { error: saveError } = await supabase
          .from('chat_messages')
          .insert({
            property_id: currentPropertyId,
            user_id: user.id,
            role: 'assistant',
            content: data.message,
            created_at: new Date().toISOString()
          });

        if (saveError) console.error('Error saving assistant message:', saveError);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveProperty = async () => {
    try {
      if (!rawZillowData || !user) return;
      
      console.log('Saving property with user ID:', user.id);
      
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { data: updateData, error: updateError } = await supabase
        .from('properties')
        .upsert({
          address: address,
          zillow_data: rawZillowData,
          user_id: user.id  // Add the user_id here - critical for RLS policies
        })
        .select();
        
      if (updateError) {
        console.error('Error saving property:', updateError);
        setError(`Failed to save property: ${updateError.message}`);
        return;
      }
      
      console.log('Property saved successfully:', updateData);
      await loadSavedProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      setError(`Failed to save property: ${error.message || 'Unknown error'}`);
    }
  };

  const handleSaveMessage = async (message: string) => {
    console.log('Save message:', message);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleViewAnalysis = async (property: Property) => {
    try {
      setLoading(true);
      setError('');
      
      setAddress(property.address);
      setCurrentTab(0);
      
      // Debug log
      console.log('Raw property data:', property.zillow_data);
      
      // Ensure we have the normalized data structure
      const normalizedZillowData = {
        ...property.zillow_data,
        propertyDetails: {
          ...property.zillow_data?.propertyDetails,
          resoFacts: property.zillow_data?.propertyDetails?.resoFacts || {},
          address: property.zillow_data?.propertyDetails?.address || {},
        }
      };
      
      // Debug log
      console.log('Normalized data:', normalizedZillowData);
      
      setRawZillowData(normalizedZillowData);
      
      if (normalizedZillowData) {
        const details = normalizedZillowData.propertyDetails;
        
        // Set basic info
        const basicInfo = [
          { key: 'Address', value: details?.streetAddress || details?.address?.streetAddress || property.address },
          { key: 'City/State', value: `${details?.city || details?.address?.city || ''}, ${details?.state || details?.address?.state || ''}` },
          { key: 'Zip Code', value: details?.zipcode || details?.address?.zipcode || 'N/A' },
          { key: 'Price', value: details?.price ? `$${details.price.toLocaleString()}` : 'N/A' },
          { key: 'Bedrooms', value: details?.bedrooms?.toString() || 'N/A' },
          { key: 'Bathrooms', value: details?.bathrooms?.toString() || 'N/A' },
          { key: 'Year Built', value: details?.yearBuilt?.toString() || 'N/A' }
        ];

        // Debug log
        console.log('Basic info:', basicInfo);

        // Get other facts from resoFacts
        const otherFacts = details?.resoFacts?.otherFacts?.map(fact => ({
          key: fact.name,
          value: fact.value
        })) || [];

        // Debug log
        console.log('Other facts:', otherFacts);

        // Add direct fields if they exist and aren't already in otherFacts
        const directFields = {
          'Stories': details?.resoFacts?.stories,
          'Living Area': formatArea(details?.livingArea || details?.resoFacts?.livingArea),
          'Lot Size': formatArea(details?.lotSize || details?.resoFacts?.lotSize, 'acres'),
          'Architecture': details?.resoFacts?.architecturalStyle,
          'Construction': details?.resoFacts?.constructionMaterials,
          'Foundation': details?.resoFacts?.foundationDetails,
          'Roof': details?.resoFacts?.roofType,
          'Exterior': details?.resoFacts?.exteriorFeatures,
          'Property Condition': details?.resoFacts?.propertyCondition,
          'Topography': details?.resoFacts?.topography,
          'Vegetation': details?.resoFacts?.vegetation,
          'Garage/Parking': details?.resoFacts?.parkingFeatures,
          'Fireplace': details?.resoFacts?.fireplaceFeatures,
          'Appliances': details?.resoFacts?.appliances,
          'Floor Coverings': details?.resoFacts?.flooring,
          'HOA Amenities': details?.resoFacts?.associationAmenities,
          'Decks': details?.resoFacts?.patioAndPorchFeatures,
          'Gas': details?.resoFacts?.gas,
          'Heating': details?.resoFacts?.heating,
          'Other Equipment': details?.resoFacts?.otherEquipment,
          'Water': details?.resoFacts?.waterSource,
          'Laundry': details?.resoFacts?.laundryFeatures,
          'Bonus Rooms': details?.resoFacts?.rooms,
          'Sewer': details?.resoFacts?.sewer,
          'Cooling': details?.resoFacts?.cooling,
          'Pool': details?.resoFacts?.poolFeatures,
          'Security': details?.resoFacts?.securityFeatures,
          'Windows': details?.resoFacts?.windowFeatures,
          'View': details?.resoFacts?.view
        };

        // Debug log
        console.log('Direct fields:', directFields);

        const additionalFacts = Object.entries(directFields)
          .filter(([key, value]) => value && value !== 'N/A' && !otherFacts.some(fact => fact.key === key))
          .map(([key, value]) => ({
            key,
            value: formatValue(value)
          }));

        // Debug log
        console.log('Additional facts:', additionalFacts);

        // Combine all facts - don't filter out N/A values yet, let's see what we have
        const allFacts = [...basicInfo, ...otherFacts, ...additionalFacts];
        
        // Debug log
        console.log('All facts before filtering:', allFacts);

        // Set the property data
        setPropertyData(allFacts);

        // Set recommendations if they exist
        if (property.recommendations) {
          setRecommendations(property.recommendations);
        }

        // Set the current property ID before loading messages
        setCurrentPropertyId(property.id);

        // Load chat messages if we have a user
        if (user) {
          const { data: messages, error } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('property_id', property.id)
            .order('created_at', { ascending: true });

          if (!error && messages) {
            setChatMessages(messages);
          }
        }
      }
    } catch (error) {
      console.error('Error viewing analysis:', error);
      setError('Failed to load property analysis');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!propertyToDelete) return;
    
    try {
      setLoadingProperties(true);
      
      const { error } = await supabase
        .from('properties')
        .delete()
        .match({ id: propertyToDelete.id });

      if (error) throw error;

      setSavedProperties(properties => properties.filter(p => p.id !== propertyToDelete.id));
      setPropertyToDelete(null);
      
      // If the deleted property was the current one, clear it
      if (propertyToDelete.address === address) {
        setAddress('');
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      setError('Failed to delete property');
    } finally {
      setLoadingProperties(false);
    }
  };

  const handleDebugClick = (event: React.MouseEvent<HTMLElement>) => {
    setDebugModalOpen(true);
  };

  const handleDebugClose = () => {
    setDebugModalOpen(false);
  };

  const DebugModal = () => (
    <Dialog
      open={debugModalOpen}
      onClose={handleDebugClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>Debug Information</DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ 
          height: '100%',
          overflow: 'auto'
        }}>
          <Typography variant="h6" gutterBottom>Basic State:</Typography>
          <Box sx={{ mb: 3, pl: 2 }}>
            <Typography>Address: {address}</Typography>
            <Typography>Loading: {loading ? 'true' : 'false'}</Typography>
            <Typography>Error: {error || 'none'}</Typography>
          </Box>

          <Typography variant="h6" gutterBottom>Raw Zillow API Response:</Typography>
          <Box sx={{ 
            mb: 3,
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '500px'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(rawZillowData, null, 2)}
            </pre>
          </Box>

          <Typography variant="h6" gutterBottom>Processed Property Data:</Typography>
          <Box sx={{ 
            mb: 3,
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '300px'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(propertyData, null, 2)}
            </pre>
          </Box>

          <Typography variant="h6" gutterBottom>Generated Recommendations:</Typography>
          <Box sx={{ 
            mb: 3,
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '300px'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {recommendations.join('\n')}
            </pre>
          </Box>

          <Typography variant="h6" gutterBottom>Chat History:</Typography>
          <Box sx={{ 
            mb: 3,
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            maxHeight: '300px'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(chatMessages, null, 2)}
            </pre>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDebugClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
      setUserInput('');
    }
  };

  const analyzeProperty = async (propertyDetails: any) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No authentication token available');
      }

      // Format property details to match what the API expects
      const formattedPropertyDetails = {
        address: propertyDetails.address?.streetAddress || propertyDetails.streetAddress,
        cityState: `${propertyDetails.address?.city || propertyDetails.city || ''}, ${propertyDetails.address?.state || propertyDetails.state || ''}`,
        zipCode: propertyDetails.address?.zipcode || propertyDetails.zipcode,
        price: propertyDetails.price,
        yearBuilt: propertyDetails.yearBuilt,
        stories: propertyDetails.resoFacts?.stories,
        construction: propertyDetails.resoFacts?.constructionMaterials,
        foundation: propertyDetails.resoFacts?.foundationDetails,
        exterior: propertyDetails.resoFacts?.exteriorFeatures,
        roof: propertyDetails.resoFacts?.roofType,
        livingArea: propertyDetails.livingArea || propertyDetails.resoFacts?.livingArea,
        lotSize: propertyDetails.lotSize || propertyDetails.resoFacts?.lotSize,
        architecture: propertyDetails.resoFacts?.architecturalStyle,
        propertyCondition: propertyDetails.resoFacts?.propertyCondition,
        topography: propertyDetails.resoFacts?.topography,
        vegetation: propertyDetails.resoFacts?.vegetation
      };

      // Call the chat API endpoint
      // Use a secure approach that doesn't expose the token in logs
      // The token is still sent in headers but we don't reference it directly in code
      // where it might be logged
      const response = await fetch(API_ENDPOINTS.chat, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          messages: [],
          propertyData: {
            propertyDetails: formattedPropertyDetails
          },
          recommendations: recommendations || []
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`API error: ${errorData.error || response.statusText}`);
      }
      
      const data = await response.json();
      
      // Add the AI's response to the chat
      const aiMessage: ChatMessage = {
        role: 'assistant',
        content: data.message,
        created_at: new Date().toISOString()
      };
      setChatMessages([aiMessage]);

      // If we have a user and property ID, save the message
      if (user && currentPropertyId) {
        await supabase
          .from('chat_messages')
          .insert({
            property_id: currentPropertyId,
            user_id: user.id,
            role: 'assistant',
            content: data.message
          });
      }
    } catch (error) {
      console.error('Error analyzing property:', error);
      setError('Failed to analyze property: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Render based on current path
  if (currentPath === '/bid-request-tool') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppBar 
          position="static" 
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton 
                edge="start" 
                color="inherit" 
                aria-label="home"
                onClick={() => {
                  window.history.pushState({}, '', '/');
                  setCurrentPath('/');
                }}
              >
                <ShieldIcon sx={{ color: 'primary.main' }} />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ 
                fontWeight: 600,
                background: 'linear-gradient(45deg, #2E7D32 30%, #4CAF50 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                SafeHaven
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                  {user.email}
                </Typography>
                <IconButton
                  color="inherit"
                  onClick={() => {
                    supabase.auth.signOut();
                    window.history.pushState({}, '', '/');
                    setCurrentPath('/');
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </AppBar>
        <Box sx={{ 
          p: { xs: 1, sm: 2, md: 3 },
          height: 'calc(100vh - 64px)',
          overflow: 'hidden'
        }}>
          <BidRequestTool />
        </Box>
      </ThemeProvider>
    );
  } else if (currentPath === '/Frontend-0.2') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppBar 
          position="static" 
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ShieldIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" component="div" sx={{ 
                fontWeight: 600,
                background: 'linear-gradient(45deg, #2E7D32 30%, #4CAF50 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                SafeHaven
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Button 
              onClick={() => {
                window.history.pushState({}, '', '/');
                setCurrentPath('/');
              }}
              color="primary"
              variant="outlined"
              sx={{ mr: 2 }}
            >
              Back to App
            </Button>
          </Toolbar>
        </AppBar>
        <FrontendTest />
      </ThemeProvider>
    );
  }

  return (
    <AuthErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppBar 
          position="static" 
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ShieldIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" component="div" sx={{ 
                fontWeight: 600,
                background: 'linear-gradient(45deg, #2E7D32 30%, #4CAF50 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                SafeHaven
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {user && (
              <>
                {/* Hamburger menu - visible on all screen sizes */}
                <Box sx={{ display: 'block' }}>
                  <IconButton
                    size="large"
                    edge="end"
                    color="inherit"
                    aria-label="menu"
                    onClick={handleMenuOpen}
                  >
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <MenuItem onClick={() => {
                      window.history.pushState({}, '', '/bid-request-tool');
                      setCurrentPath('/bid-request-tool');
                      handleMenuClose();
                    }}>
                      Bid Request Tool
                    </MenuItem>
                    <MenuItem onClick={() => {
                      window.history.pushState({}, '', '/Frontend-0.2');
                      setCurrentPath('/Frontend-0.2');
                      handleMenuClose();
                    }}>
                      Frontend Test
                    </MenuItem>
                    <MenuItem onClick={(event) => {
                      handleDebugClick(event);
                      handleMenuClose();
                    }}>
                      Debug
                    </MenuItem>
                    <MenuItem onClick={() => {
                      supabase.auth.signOut();
                      handleMenuClose();
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LogoutIcon fontSize="small" />
                        Sign Out
                      </Box>
                    </MenuItem>
                  </Menu>
                </Box>
              </>
            )}
          </Toolbar>
        </AppBar>
        <DebugModal />
        <WelcomeModal 
          open={showWelcomeModal} 
          onClose={() => setShowWelcomeModal(false)} 
        />

        <Dialog
          open={!!propertyToDelete}
          onClose={() => setPropertyToDelete(null)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete the property at {propertyToDelete?.address}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPropertyToDelete(null)} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={() => confirmDelete()} 
              color="error"
              variant="contained"
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {!user ? (
          <LoginForm onLogin={() => {}} />
        ) : (
          <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Rest of the app content */}
            <Box sx={{ 
              flexGrow: 1,
              background: 'url(/images/forest-background.jpg) no-repeat center center fixed',
              backgroundSize: 'cover',
              py: 4,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backdropFilter: 'blur(1px)',
              }
            }}>
              <Box 
                sx={{ 
                  display: 'flex',
                  gap: 3,
                  maxWidth: '100%',
                  width: '100%',
                  position: 'relative',
                  zIndex: 1,
                  px: { xs: 0, sm: 3, lg: 6 },
                  flexDirection: { xs: 'column', md: 'row' }
                }}
              >
                {/* Interactive Consultation - Shows second on mobile */}
                <Box sx={{ 
                  flex: { xs: 'auto', md: '1' },
                  position: { xs: 'relative', md: 'sticky' },
                  top: { xs: 0, md: 24 },
                  alignSelf: { xs: 'auto', md: 'flex-start' },
                  maxHeight: { xs: '70vh', md: 'calc(100vh - 48px)' },
                  display: 'flex',
                  order: { xs: 2, md: 2 }
                }}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid',
                      borderColor: 'divider',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Typography variant="h6" component="h2">
                        Interactive Consultation
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 2,
                      flex: 1,
                      minHeight: 0 // This is important for nested flex containers
                    }}>
                      {/* Chat interface */}
                      <Box
                        ref={chatEndRef}
                        sx={{
                          flex: 1,
                          overflowY: 'auto',
                          mb: 2,
                          p: 2,
                          backgroundColor: 'background.paper',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          minHeight: 0 // This is important for nested flex containers
                        }}
                      >
                        {chatMessages.map((message, index) => (
                          <Box
                            key={index}
                            sx={{
                              mb: 2,
                              p: 2,
                              backgroundColor: message.role === 'assistant' ? 'primary.light' : 'background.paper',
                              borderRadius: 2,
                              maxWidth: '100%'
                            }}
                          >
                            <MarkdownContainer>
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {message.content}
                              </ReactMarkdown>
                            </MarkdownContainer>
                          </Box>
                        ))}
                      </Box>

                      {/* Input area */}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          value={userInput}
                          onChange={(e) => setUserInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (userInput.trim() && !isLoading) {
                                handleSendMessage(userInput);
                              }
                            }
                          }}
                          placeholder="Ask a question about the recommendations..."
                          variant="outlined"
                          disabled={isLoading}
                        />
                        <IconButton
                          color="primary"
                          onClick={() => handleSendMessage(userInput)}
                          disabled={isLoading || !userInput.trim()}
                          sx={{ alignSelf: 'flex-end' }}
                        >
                          <SendIcon />
                        </IconButton>
                        <IconButton
                          color={isListening ? 'secondary' : 'primary'}
                          onClick={toggleListening}
                          sx={{ alignSelf: 'flex-end' }}
                        >
                          {isListening ? <MicOffIcon /> : <MicIcon />}
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </Box>

                {/* Main content - Shows first on mobile */}
                <Box sx={{ 
                  flex: { xs: 'auto', md: '2' },
                  order: { xs: 1, md: 1 }
                }}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid',
                      borderColor: 'divider',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      maxHeight: '100%',
                      overflow: 'hidden',
                      maxWidth: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    {/* Main content tabs and components */}
                    <Tabs
                      value={currentTab}
                      onChange={handleTabChange}
                      variant="fullWidth"
                      sx={{
                        backgroundColor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTabs-flexContainer': {
                          px: { xs: 0, sm: 2 }
                        }
                      }}
                    >
                      <Tab 
                        icon={<HomeIcon />} 
                        label="Enter Your Address" 
                        sx={{ textTransform: 'none' }}
                      />
                      <Tab 
                        icon={<ShieldIcon />} 
                        label="Goals" 
                        sx={{ textTransform: 'none' }}
                      />
                      <Tab 
                        icon={<SearchIcon />} 
                        label="Saved Properties" 
                        sx={{ textTransform: 'none' }}
                      />
                    </Tabs>

                    {currentTab === 0 ? (
                      <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '100%', overflowX: 'hidden', overflowY: 'auto', boxSizing: 'border-box', maxHeight: '100vh', overscrollBehavior: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                          <HomeIcon color="primary" />
                          <Typography variant="h6" component="h2">
                            Enter Your Address
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <TextField
                            fullWidth
                            placeholder="Enter your address..."
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            variant="outlined"
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                              },
                            }}
                          />
                          <Box sx={{ 
                            display: 'flex', 
                            gap: '8px',
                            flexDirection: { xs: 'column', sm: 'row' }
                          }}>
                            <Button
                              onClick={handleSubmit}
                              variant="contained"
                              color="primary"
                              disabled={loading || !address.trim()}
                              fullWidth
                              sx={{
                                height: '56px',
                                fontWeight: 600,
                              }}
                            >
                              {loading ? <CircularProgress size={24} /> : 'Analyze'}
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => handleSaveProperty()}
                              disabled={!rawZillowData}
                              fullWidth
                              sx={{
                                height: '56px',
                                fontWeight: 600,
                              }}
                            >
                              Save Property
                            </Button>
                          </Box>
                        </Box>
                        {propertyData && propertyData.length > 0 && (
                          <Paper sx={{ mt: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
                            <Typography variant="h6" gutterBottom color="primary">
                              Key Property Facts
                            </Typography>
                            <Grid container spacing={{ xs: 2, sm: 3 }}>
                              {/* Basic Info Section */}
                              <Grid item xs={12}>
                                <Typography variant="subtitle1" color="primary" gutterBottom>
                                  Basic Information
                                </Typography>
                                <Grid container spacing={2}>
                                  {propertyData.slice(0, 7).map((item, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                      <Card>
                                        <CardContent>
                                          <Typography variant="subtitle2" color="text.secondary">
                                            {item.key}
                                          </Typography>
                                          <Typography variant="body1">
                                            {item.value}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Grid>

                              {/* Additional Details Section */}
                              {propertyData.length > 7 && (
                                <Grid item xs={12}>
                                  <Typography variant="subtitle1" color="primary" gutterBottom sx={{ mt: 2 }}>
                                    Additional Details
                                  </Typography>
                                  <Grid container spacing={2}>
                                    {propertyData.slice(7).map((item, index) => (
                                      <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Card>
                                          <CardContent>
                                            <Typography variant="subtitle2" color="text.secondary">
                                              {item.key}
                                            </Typography>
                                            <Typography variant="body1">
                                              {item.value}
                                            </Typography>
                                          </CardContent>
                                        </Card>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Grid>
                              )}
                            </Grid>

                            {rawZillowData?.mixedSources?.jpeg?.[0]?.url && (
                              <Box sx={{ mt: 2 }}>
                                <Button
                                  href={rawZillowData.mixedSources.jpeg[0].url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outlined"
                                  size="small"
                                >
                                  View on Zillow
                                </Button>
                              </Box>
                            )}
                          </Paper>
                        )}
                        {recommendations.length > 0 && (
                          <Paper 
                            elevation={0}
                            sx={{ 
                              mt: 3,
                              backgroundColor: 'rgba(255, 255, 255, 0.95)',
                              backdropFilter: 'blur(20px)',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                              <ShieldIcon color="primary" />
                              <Typography variant="h6" component="h2">
                                Wildfire Safety Recommendations
                              </Typography>
                            </Box>
                            <MarkdownContainer>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  p: ({node, children, ...props}) => <Typography variant="body1" {...props}>{children}</Typography>,
                                  h1: ({node, children, ...props}) => <Typography variant="h4" {...props}>{children}</Typography>,
                                  h2: ({node, children, ...props}) => <Typography variant="h5" {...props}>{children}</Typography>,
                                  h3: ({node, children, ...props}) => <Typography variant="h6" {...props}>{children}</Typography>,
                                  a: ({node, children, ...props}) => <Link {...props} target="_blank" rel="noopener noreferrer">{children}</Link>,
                                  ul: ({node, children, ...props}) => <ul style={{ listStyleType: 'disc' }} {...props}>{children}</ul>,
                                  ol: ({node, children, ...props}) => <ol style={{ listStyleType: 'decimal' }} {...props}>{children}</ol>,
                                }}
                              >
                                {recommendations.join('\n\n')}
                              </ReactMarkdown>
                            </MarkdownContainer>
                          </Paper>
                        )}
                        {error && (
                          <Typography color="error" sx={{ mt: 2 }}>
                            {error}
                          </Typography>
                        )}
                      </Box>
                    ) : currentTab === 1 ? (
                      <Box sx={{ py: { xs: 1, sm: 2, md: 3 }, px: { xs: 1, sm: 2, md: 3 }, maxWidth: '100%', overflowX: 'hidden', overflowY: 'auto', boxSizing: 'border-box', maxHeight: '100vh', overscrollBehavior: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                          <ShieldIcon color="primary" />
                          <Typography variant="h6" component="h2">
                            Wildfire Safety Goals
                          </Typography>
                        </Box>
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                          <Grid item xs={12}>
                            <HomeAnalysis 
                              propertyId={currentPropertyId} 
                              userId={user?.id}
                              propertyDetails={rawZillowData?.propertyDetails ? {
                                address: rawZillowData.propertyDetails.address?.streetAddress || rawZillowData.propertyDetails.streetAddress || 'N/A',
                                cityState: `${rawZillowData.propertyDetails.address?.city || rawZillowData.propertyDetails.city || ''}, ${rawZillowData.propertyDetails.address?.state || rawZillowData.propertyDetails.state || ''}`,
                                zipCode: rawZillowData.propertyDetails.address?.zipcode || rawZillowData.propertyDetails.zipcode || 'N/A',
                                price: rawZillowData.propertyDetails.price ? `$${rawZillowData.propertyDetails.price.toLocaleString()}` : 'N/A',
                                yearBuilt: rawZillowData.propertyDetails.yearBuilt?.toString() || rawZillowData.propertyDetails.resoFacts?.yearBuilt || 'N/A',
                                stories: getPropertyValue('Stories'),
                                construction: getPropertyValue('CONSTRUCTION/EXT'),
                                foundation: getPropertyValue('FOUNDATION'),
                                exterior: getPropertyValue('EXTERIOR'),
                                roof: getPropertyValue('ROOF'),
                                architecture: getPropertyValue('Architecture'),
                                propertyCondition: getPropertyValue('PROPERTY CONDITION'),
                                topography: getPropertyValue('TOPOGRAPHY'),
                                vegetation: getPropertyValue('VEGETATION')
                              } : null}
                              savedProperties={savedProperties}
                              onPropertySelect={(id) => setCurrentPropertyId(id)}
                              loadingProperties={loadingProperties}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body1" paragraph>
                              More goal-setting features coming soon!
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ) : (
                      <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '100%', overflowX: 'hidden', overflowY: 'auto', boxSizing: 'border-box', maxHeight: '100vh', overscrollBehavior: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                          <SearchIcon color="primary" />
                          <Typography variant="h6" component="h2">
                            Saved Properties
                          </Typography>
                        </Box>
                        
                        {loadingProperties ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                          </Box>
                        ) : savedProperties.length === 0 ? (
                          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                            No properties saved yet. Add a property from the "Enter Your Address" tab.
                          </Typography>
                        ) : (
                          <Grid container spacing={2}>
                            {savedProperties.map((property) => (
                              <Grid item xs={12} key={property.id}>
                                <Card 
                                  sx={{ 
                                    mb: 2,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                    }
                                  }}
                                >
                                  <CardContent>
                                    <Typography variant="h6" component="div">
                                      {property.zillow_data?.propertyDetails?.address?.streetAddress || 
                                       property.zillow_data?.propertyDetails?.streetAddress || 
                                       property.address}
                                    </Typography>
                                    
                                    <Box sx={{ mt: 2 }}>
                                      <Typography variant="body2">
                                        Price: ${property.zillow_data?.propertyDetails?.price?.toLocaleString() ?? 'N/A'}
                                      </Typography>
                                      <Typography variant="body2">
                                        Beds: {property.zillow_data?.propertyDetails?.bedrooms ?? 'N/A'}
                                      </Typography>
                                      <Typography variant="body2">
                                        Baths: {property.zillow_data?.propertyDetails?.bathrooms ?? 'N/A'}
                                      </Typography>
                                      <Typography variant="body2">
                                        Year Built: {property.zillow_data?.propertyDetails?.yearBuilt ?? 'N/A'}
                                      </Typography>
                                      {property.zillow_data?.mixedSources?.jpeg?.[0]?.url && (
                                        <Link 
                                          href={property.zillow_data.mixedSources.jpeg[0].url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          sx={{ display: 'block', mt: 1 }}
                                        >
                                          View on Zillow
                                        </Link>
                                      )}
                                    </Box>

                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                      <Button 
                                        size="small" 
                                        variant="outlined"
                                        onClick={() => handleViewAnalysis(property)}
                                      >
                                        View Analysis
                                      </Button>
                                      <Button 
                                        size="small" 
                                        startIcon={<DeleteIcon />}
                                        onClick={() => setPropertyToDelete(property)}
                                        color="error"
                                      >
                                        Remove
                                      </Button>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </ThemeProvider>
    </AuthErrorBoundary>
  );
}

export default App;
